import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, FormikHelpers, FormikValues } from 'formik';
import * as yup from 'yup';
import queryString from 'query-string';

import { FormAlert } from 'components/CASAForm';
import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { FormStep, IFormAlertType } from 'types/forms/formTypes';
import { AuBreadcrumbs, IAuBreadcrumbItem } from 'components/DTA';
import { processUrlParameters } from 'utilities';
import { FORM_ERROR_MESSAGES } from 'internalConstants';

import { OrganisationContext } from 'contexts/OrganisationContext';
import { DroneEditStepProps, DroneRegistrationFormData, FinalStepProps } from 'types/forms/droneRegistrationTypes';
import { BeginStep, DroneListStep } from 'forms/Drones/DroneRegistration';
import { useDroneService } from 'services';
import { DroneContext } from 'contexts';
import { ExitModal } from 'components/CASAForm/ExitModal';
import { DroneEditStep, FinalStep, droneValidationSchema} from 'forms/Drones/Common';

const DroneRegistration: React.FC = () => {
  const [isBackButtonClicked, setBackbuttonPress] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const {
    referenceData,
    isOrganisationRegistration,
    checkIfOtherManufacturer,
    checkIfOtherModel,
    checkIfHomeBuilt,
    dashboardUrl,
    individualDrones,
    organisationDrones,
    setContextAlert,
    setSubmissionInProgress,
    checkSerialNumberPrefix
  } = useContext(DroneContext);
  const { organisationDetailsResponse } = useContext(OrganisationContext);
  const droneService = useDroneService();

  useEffect(() => {
    window.history.pushState(window.location.search, '');
    window.addEventListener('popstate', onBackButtonEvent);
  }, []);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!isBackButtonClicked) {
      setShowModal(true);
    }
  };

  const toggle = () => {
    setShowModal(false);
    window.history.pushState(window.location.search, '');
    setBackbuttonPress(false);
  };

  const OK = () => {
    setShowModal(false);
    setBackbuttonPress(true);
    if (isOrganisationRegistration) {
      window.history.go(-2);
    }
    else {
      window.history.back();
    }
  };

  const exitModalParameters = {
    isOpen: showModal,
    toggleFunction: toggle,
    OKFunction: OK
  };

  const initialValues: DroneRegistrationFormData = {
    drones: [],
    declaration: false,
    totalAmount: 0
  };

  useEffect(() => {
    document.title = 'Drone registration';
    // If westpac returns non succesful payment set an error alert
    // into the context for display on the drone list step
    const returnParameters = queryString.parse(window.location.search);
    if (returnParameters.summaryCode !== '0' && returnParameters.customRef) {
      setContextAlert({
        as: 'error',
        title: 'There was a problem with your card',
        message: 'Please check your card details or use a different card. Your application has been saved and can be submitted again from your profile screen.'
      });
    }
  }, []);

  // Steps and step validation associated with the form
  const STEPS: FormStep[] = [
    {
      id: 'BeginRegistration',
      render: () => (
        <BeginStep />
      )
    },
    {
      id: 'DroneEdit',
      render: (props?: DroneEditStepProps) => (
        <DroneEditStep {...props} applicationName='Drone Registration' />
      ),
      validationSchema: yup.object().shape({
        drones: droneValidationSchema(referenceData, isOrganisationRegistration, individualDrones, organisationDrones, checkSerialNumberPrefix)
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      })
    },
    {
      id: 'DroneList',
      render: () => (
        <DroneListStep />
      ),
      validationSchema: yup.object().shape({
        drones: droneValidationSchema(referenceData, isOrganisationRegistration, individualDrones, organisationDrones, checkSerialNumberPrefix)
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        declaration: yup.boolean()
          .required(FORM_ERROR_MESSAGES.DECLARATION)
          .oneOf([true], FORM_ERROR_MESSAGES.DECLARATION)
      })
    },
    {
      id: 'Final',
      render: (props?: FinalStepProps) => (
        <FinalStep {...props} />
      ),
      skipNavigation: true
    }
  ];

  const handleSubmit = async (values: DroneRegistrationFormData, actions: FormikHelpers<DroneRegistrationFormData & FormikValues>): Promise<IFormAlertType | boolean | null> => {
    setSubmissionInProgress(true);
    const { setSubmitting } = actions;

    // set some values required for submission
    values.drones.forEach(drone => {
      drone.intendedUse === 'recreational' ? drone.formCode = 'DRONE_R_REC' : drone.formCode = 'DRONE_R_COM';
      if (!checkIfOtherManufacturer(drone.manufacturerCode)) {
        drone.manufacturer = referenceData?.value.DRONE_MANUFACTURER.find(item => item.casa_code === drone.manufacturerCode)?.casa_name;
      }
      if (!checkIfOtherManufacturer(drone.manufacturerCode) && !checkIfOtherModel(drone.modelCode) && !checkIfHomeBuilt(drone.manufacturerCode)) {
        drone.model = referenceData?.value.DRONE_MODEL.find(item => item.casa_code === drone.modelCode)?.casa_name;
      }
      drone.category = referenceData?.value.DRONE_CATEGORY.find(item => item.casa_code === drone.categoryCode)?.casa_name;
      drone.weightClass = referenceData?.value.DRONE_WEIGHT_CLASS.find(item => item.casa_code === drone.weightClassCode)?.casa_name;
    });
    values.totalAmount = values.drones.reduce((accumulator, current) => accumulator + (current.fee || 0), 0);

    // Send request to Submission service
    try {
      const response = await droneService.submitDroneRegistration({
        orgId: organisationDetailsResponse?.value?.value[0]?.accountid,
        organisationArn: organisationDetailsResponse?.value?.value[0]?.casa_arn,
        formData: values
      });

      // If request failed return error to the user
      if (!response || !response.wasSuccessful) {
        setSubmitting(false);
        setSubmissionInProgress(false);
        return {
          as: 'error',
          title: 'Unable to complete application online',
          message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
        };
      }
      else {
        // If successful but requires payment redirect to the payment gateway
        if (response.paymentToken) {
          // Redirect to payment screen
          window.location.href = B2CSettings.items.WestpacQuickWebURL + response.paymentToken;
          await new Promise(resolve => setTimeout(resolve, 2000));
          return null;
        }
        else {
          setSubmitting(false);
          setSubmissionInProgress(false);
          // If successful but no payment required then return success to the user
          return {
            as: 'success',
            title: 'Registration successful',
            message: ''
          };
        }
      }
    }
    catch (e: any) {
      setSubmitting(false);
      setSubmissionInProgress(false);
      return {
        as: 'error',
        title: 'Unable to complete application online',
        message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
      };
    }
  };

  const getDraftRecordId = () => {
    // process url parameters that require base64 decoding
    const params = processUrlParameters(window.location.search);
    if (params && params.recordid) {
      return params.recordid;
    }
    // check for cancelled or failed payment and load draft if true
    const returnParameters = queryString.parse(window.location.search);
    if (returnParameters.summaryCode !== '0' && returnParameters.customRef) {
      return returnParameters.customRef;
    }
    return '';
  };

  const organisationId = isOrganisationRegistration ? organisationDetailsResponse?.value?.value[0]?.accountid : '';

  const getReturnUrl = () => {
    if (isOrganisationRegistration) {
      return `${window.location.origin}/forms/orgmanagerpa?standalone=${B2CSettings.items.standalone}&casaorgid=${organisationId}`;
    }
    return `${window.location.origin}/forms/indmanagerpa?standalone=${B2CSettings.items.standalone}`;
  };

  // TODO: this needs to be looked at, breadcrumbs shouldn't be hard coded.
  // Options are use breadcrumbs passed in params or eventually have an SPA
  // wide breadcrumb solution
  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: dashboardUrl, text: 'myCASA' },
    { link: getReturnUrl(), text: 'Manage registered drones' },
    { text: <><abbr title="Remotely piloted aircraft">Drone</abbr> registration</> }
  ];

  const getDraftSuccessReturnUrl = () => {
    return `${getReturnUrl()}&saveSuccess=1`;
  };

  return (
    <>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={STEPS}
        draftRecordId={getDraftRecordId()}
        draftSuccessReturnUrl={getDraftSuccessReturnUrl()}
        draftFailReturnUrl={getReturnUrl()}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps }: IFormContextType) => (
            <Form name='Drone registration'>
              <FormAlert htmlFor='Drone registration' />
              <ExitModal exitModalParams={exitModalParameters} />
              {steps[currentStepIndex].render(currentStepProps)}
            </Form>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </>
  );
};

export default DroneRegistration;